import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showSuccessToast } from '@utils'
import { useLanguage } from '@context'
import { useApiErrorHandler } from '../../../useApiErrorHandler'

interface DeleteHashtagParams {
  id: string
}

const deleteHashtag = async ({ id }: DeleteHashtagParams): Promise<void> => {
  await api.delete(`/hashtag/${id}`)
}

export const useDeleteHashtag = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const handleApiError = useApiErrorHandler()

  return useMutation<void, Error, DeleteHashtagParams>({
    mutationFn: deleteHashtag,
    onSuccess: async (_, variables) => {
      showSuccessToast(t('toast.success'), t('toast.hashtagDeleted'))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['hashtags'] }),
        // Marked stale without asking again: the screen is still open and
        // a new request for the deleted item would answer « not found ».
        queryClient.invalidateQueries({ queryKey: ['hashtag', variables.id], refetchType: 'none' }),
        queryClient.invalidateQueries({
          queryKey: ['hashtag-contacts', { id: variables.id }],
          refetchType: 'none',
        }),
      ])
    },
    // A refused deletion says so instead of failing in silence.
    onError: handleApiError,
  })
}

